import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState, useEffect} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import LLMFeedbackModal from 'llm/feedback/LLMFeedbackModal';
import {GRAY_DARKER, GRAY} from 'ui-library/Colors';
import runModal from 'ui-library/runModal';

export interface AnnotationAssistantFeedbackProps {
  userPrompt: string;
  traceId: string;
}

export default function AnnotationAssistantFeedback({
  userPrompt,
  traceId,
}: AnnotationAssistantFeedbackProps): JSX.Element {
  const [usefulSuggestion, setUsefulSuggestion] = useState<boolean | null>(null);
  useEffect(() => {
    // If there is a new suggest, reset the useful state.
    setUsefulSuggestion(null);
  }, [traceId]);

  const openFeedbackModal = (): void => {
    runModal(LLMFeedbackModal, {
      title: 'Role annotation assistant feedback',
      optionalContextItems: [{name: 'prompt', value: userPrompt}],
      requiredContextItems: [
        {
          name: 'Useful',
          value: String(usefulSuggestion),
        },
        {
          name: 'Trace ID',
          value: traceId,
        },
      ],
    });
  };

  return (
    <div className={css(styles.feedback)}>
      <p>Was this suggestion helpful?</p>
      <div className={css(styles.feedbackThumbs)}>
        <a
          className={classNames(
            'fa fa-thumbs-up',
            css(styles.thumbsLink, usefulSuggestion !== true && styles.thumbsLinkInactive)
          )}
          onClick={() => {
            if (usefulSuggestion === true) {
              setUsefulSuggestion(null);
            } else {
              trackEvent(STORABLE_ANALYTICS_ACTIONS.SUBMITTED_ROLE_ANNOTATION_AI_SUGGESTION_USEFULNESS, {
                useful: true,
                traceId,
              });
              setUsefulSuggestion(true);
            }
          }}
        />
        <a
          className={classNames(
            'fa fa-thumbs-down',
            css(styles.thumbsLink, usefulSuggestion !== false && styles.thumbsLinkInactive)
          )}
          onClick={() => {
            if (usefulSuggestion === false) {
              setUsefulSuggestion(null);
            } else {
              trackEvent(STORABLE_ANALYTICS_ACTIONS.SUBMITTED_ROLE_ANNOTATION_AI_SUGGESTION_USEFULNESS, {
                useful: false,
                traceId,
              });
              setUsefulSuggestion(false);
            }
          }}
        />
        <a
          role="button"
          tabIndex={0}
          className={css(styles.feedbackModalLink)}
          onClick={openFeedbackModal}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              openFeedbackModal();
            }
          }}
        >
          Tell us more
        </a>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  feedback: {
    display: 'flex',
    justifyContent: 'space-between',
    color: GRAY_DARKER,
  },
  feedbackThumbs: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 4,
    alignItems: 'center',
  },
  thumbsLink: {
    fontSize: 14,
    display: 'inline',
    ':hover': {
      textDecoration: 'none',
    },
  },
  thumbsLinkInactive: {
    color: GRAY,
  },
  feedbackModalLink: {
    marginLeft: 6,
  },
});
